import { kebabCase } from 'lodash'
import { DataItem } from 'scrivito'

import { ensureString } from './ensureString'
import { AvatarSize } from '@/Atomic/Atoms/AvatarProfile'
import { Avatar } from '@/Atomic/Molecules/Avatar'
import { PagePermaLink } from '@/const'
import { DataClassName } from '@/Data/const'
import { DataBinary } from '@/utils/dataBinaryToUrl'
import { getDataClassIcon } from '@/utils/image/getDataClassIcon'

export const getOverviewPageLink: (dataClassName: string) => string = (
  dataClassName,
) => {
  switch (dataClassName) {
    case DataClassName.ACTIVITY:
      return PagePermaLink.ACTIVITIES
    case DataClassName.COMPANY:
      return PagePermaLink.COMPANIES
    case DataClassName.DOCUMENT:
      return PagePermaLink.DOCUMENTS
    case DataClassName.LEAD:
      return PagePermaLink.LEADS
    case DataClassName.LIST:
      return PagePermaLink.LISTS
    case DataClassName.OPPORTUNITY:
      return PagePermaLink.OPPORTUNITIES
    case DataClassName.ORDER:
      return PagePermaLink.ORDERS
    case DataClassName.PERSON:
      return PagePermaLink.PERSONS
    case DataClassName.QUOTE:
      return PagePermaLink.QUOTES
    case DataClassName.TICKET:
      return PagePermaLink.TICKETS
    case DataClassName.TIMERECORD:
      return PagePermaLink.TIME_TRACKING
    default:
      return ''
  }
}

export const getDataItemTitle = (dataItem: DataItem | null | undefined) => {
  switch (dataItem?.dataClassName()) {
    case DataClassName.ACTIVITY:
      return dataItem.get('subject') as string
    case DataClassName.ADDRESS: {
      // Construct address title from street, postalCode, city
      const street = dataItem.get('street') as string
      const postalCode = dataItem.get('postalCode') as string
      const city = dataItem.get('city') as string
      const parts = [street, postalCode, city].filter(Boolean)
      return parts.join(', ') || (dataItem.get('title') as string)
    }
    case DataClassName.COMPANY:
      return dataItem.get('name') as string
    case DataClassName.CONTACT:
      return dataItem.get('name') as string
    case DataClassName.DOCUMENT:
      return dataItem.get('displayTitle') as string
    case DataClassName.FILTER:
      return dataItem.get('title') as string
    case DataClassName.FOLLOW_UP:
      return dataItem.get('subject') as string
    case DataClassName.GLOBAL_RESULT:
      return dataItem.get('title') as string
    case DataClassName.INDUSTRY:
      return dataItem.get('name') as string
    case DataClassName.INVOICE:
      return dataItem.get('displayTitle') as string
    case DataClassName.LEAD:
      return dataItem.get('name') as string
    case DataClassName.LIST:
      return dataItem.get('title') as string
    case DataClassName.OPPORTUNITY:
      return dataItem.get('keyword') as string
    case DataClassName.ORDER:
      return dataItem.get('keyword') as string
    case DataClassName.PERSON:
      return dataItem.get('name') as string
    case DataClassName.PROJECT:
      return dataItem.get('displayTitle') as string
    case DataClassName.QUOTE:
      return dataItem.get('keyword') as string
    case DataClassName.REGION:
      return dataItem.get('name') as string
    case DataClassName.SCHEMA:
      return dataItem.get('name') as string
    case DataClassName.SOCIAL_NETWORK:
      return dataItem.get('displayTitle') as string
    case DataClassName.TICKET:
      return dataItem.get('keyword') as string
    default:
      return null
  }
}

export const getSchemaName = (dataClassName: string) => {
  return kebabCase(dataClassName.replace(/Relation/g, ''))
}

export interface EntityIconConfig {
  avatarSize?: AvatarSize
  className?: string
}

export const getEntityIconElementByDataClass = (
  dataItem: DataItem,
  config?: EntityIconConfig,
) => {
  return getEntityIconElement(dataItem, config)
}

export const getEntityIconElementByEntity = (
  dataItem: DataItem,
  config?: EntityIconConfig,
) => {
  const entity = ensureString(dataItem.get('entity'))
  return renderEntityIconElement(entity, config)
}

export const getEntityIconElement = (
  dataItem: DataItem,
  config: EntityIconConfig = {},
) => {
  return renderEntityIconElement(dataItem.dataClassName()!, config, dataItem)
}

const renderEntityIconElement = (
  dataClassName: string,
  config: EntityIconConfig = {},
  dataItem?: DataItem,
) => {
  const { avatarSize, className } = config

  if (isProfileEntity(dataClassName)) {
    return (
      <Avatar
        avatar={dataItem?.get('image') as DataBinary | undefined}
        icon={getDataClassIcon(dataClassName)}
        size={avatarSize}
        initials={getDataItemInitials(dataItem)}
        initialsColorSeed={dataItem?.id()}
        className={className}
      />
    )
  }

  return (
    <Avatar
      icon={getDataClassIcon(dataClassName)}
      size={avatarSize}
      className={className}
    />
  )
}

export const isProfileEntity = (dataClassName: string) => {
  return [
    DataClassName.COMPANY,
    DataClassName.CONTACT,
    DataClassName.PERSON,
  ].includes(dataClassName as DataClassName)
}

export const getDataItemInitials = (
  dataItem?: DataItem | null,
): string | undefined => {
  switch (dataItem?.dataClassName()) {
    case DataClassName.COMPANY:
      return getInitialsFromParts([dataItem.get('name')], 1)
    case DataClassName.CONTACT:
    case DataClassName.PERSON: {
      const explicitInitials = ensureString(dataItem.get('initials')).trim()
      if (explicitInitials) return explicitInitials.toUpperCase()

      return getInitialsFromParts(
        [dataItem.get('givenName'), dataItem.get('familyName')],
        2,
      )
    }
    default:
      return undefined
  }
}

const getInitialsFromParts = (parts: unknown[], maxLength: number) => {
  const initials = parts
    .map((part) => ensureString(part).trim())
    .filter(Boolean)
    .slice(0, maxLength)
    .map((part) => part[0]!.toUpperCase())
    .join('')

  return initials || undefined
}
